import React, { useState } from 'react';
import { Button, Card, Chip, Collapse, Divider, Grid, Icon, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { IDialogActions } from '../dialog-form/DialogForm';
import { ISwitchInputProps } from '../switchInput/SwitchInput';
import { useTranslation } from 'react-i18next';


interface IToolBarProps {
    children: React.ReactNode;
    clickSearch: () => void;
    clickClearFilter: () => void;
    actions?: IDialogActions[] | undefined;
    chipFilters: IChipFilter[];
    kanbanSwitch?: ISwitchInputProps | undefined
}

export interface IChipFilter {
    label: string;
    value: any;
}

export const ToolBar: React.FC<IToolBarProps> = ({ children, clickSearch, clickClearFilter, actions, chipFilters, kanbanSwitch }) => {
    const [filterOpen, setFilterOpen] = useState(false);
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const mdDown = useMediaQuery(theme.breakpoints.down('md'));
    const LocalLabel = !kanbanSwitch?.checked ? 'Kanban' : 'List';
    const toggleViewIcon = kanbanSwitch?.checked ? 'format_list_bulleted' : 'view_kanban_outlined';

    const { t } = useTranslation();

    const handleFilterOpen = () => {
        setFilterOpen(oldFilterOpen => !oldFilterOpen);
    };

    const handleSearch = () => {
        setFilterOpen(oldFilterOpen => !oldFilterOpen);
        clickSearch();
    };

    const handleClearFilter = () => {
        clickClearFilter();
    };

    const handleAction = (setAction: () => void) => {
        setAction();
    };


    return (
        <React.Fragment>
            <Card sx={{ display: 'flex', mb: 1, borderRadius: 1, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                <Grid container p={2} sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
                    <Grid item xs={12} md={10} display='flex' gap={1} flexDirection={smDown || mdDown ? 'column' : 'row'}>
                        {actions ? actions.map((action, index) => {
                            return (
                                <Button key={index} id={action.id} size='small' variant='outlined' color={action.iconColor} startIcon={<Icon>{action.icon}</Icon>} onClick={() => handleAction(action.setAction)} sx={{ minWidth: 150, fontWeight: 700 }}>{action.text}</Button>
                            );
                        }) : undefined}
                        <Button
                            variant='outlined'
                            size='small'
                            startIcon={<Icon>{filterOpen ? 'filter_alt_off_outlined' : 'filter_alt_outlined'}</Icon>}
                            onClick={handleFilterOpen}
                            color={filterOpen ? 'secondary' : 'primary'}
                            sx={{ minWidth: 150, fontWeight: 700 }}
                        >
                            {filterOpen ? t('toolBar.closeFilters') : t('toolBar.filters')}
                        </Button>
                        {!filterOpen ? chipFilters.map((filter, index) => {
                            if (filter.value) {
                                return (
                                    <Chip key={index} label={filter.label + ': ' + filter.value} color='secondary' variant="outlined" size='medium' />
                                );
                            }
                        }) : (undefined)}
                    </Grid>
                    {kanbanSwitch && (
                        <Grid
                            display='flex'
                            item
                            xs={12}
                            md={2}
                            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                            alignItems='center'
                            sx={{ ml: 'auto' }}
                        >
                            <Tooltip title={LocalLabel}>
                                <span>
                                    <IconButton
                                        id={kanbanSwitch?.id ?? ''}
                                        size='small'
                                        color={kanbanSwitch?.color ?? 'info'}
                                        disabled={kanbanSwitch?.disabled ?? false}
                                        autoFocus={kanbanSwitch?.autoFocus ?? false}
                                        onClick={() => kanbanSwitch?.setChecked?.(!kanbanSwitch?.checked)}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'currentColor',
                                            borderRadius: 1,
                                            width: 40,
                                            height: 34,
                                        }}
                                    >
                                        <Icon fontSize='small'>{toggleViewIcon}</Icon>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>
                    )}
                </Grid>

            </Card>
            <Collapse in={filterOpen} timeout='auto' unmountOnExit>
                <Card sx={{ display: 'flex', mb: 1, p: 2, borderRadius: 1, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={12} >
                            {children}
                        </Grid>
                        <Grid item xs={12} md={12} >
                            <Divider />
                        </Grid>
                        <Grid item xs={12} md={12} display='flex' gap={1} justifyContent='flex-end'>
                            <Button variant='outlined' size='small' startIcon={<Icon>search</Icon>} onClick={handleSearch} >
                                {t('toolBar.search')}
                            </Button>
                            <Button variant='outlined' color='secondary' size='small' startIcon={<Icon>search_off</Icon>} onClick={handleClearFilter} >
                                {t('toolBar.clear')}
                            </Button>
                        </Grid>
                    </Grid>
                </Card>
            </Collapse>
        </React.Fragment >
    );
};
